import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Modal,
    Box,
    Stack,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Switch,
    LinearProgress,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { AgeInput } from '~/shared/ui/inputs/AgeInput';
import { CallToAction } from '~/shared/ui/inputs/CallToAction';
import { CheckboxWithLabel } from '~/shared/ui/checkboxes/CheckboxWithLabel';
import { DevicesSelect } from '~/shared/ui/selectors/DevicesSelect';
import { GeoSelect2 } from '~/shared/ui/selectors/GeoSelect2';
import { MultipleTextInput } from '~/shared/ui/inputs/MultipleTextInput';
import { ScheduleModal } from '~/shared/ui/modals/ScheduleModal';
import { LanguageSelector2 } from '~/shared/ui/selectors/LanguageSelector2';
import { AuditorySelect2 } from '~/shared/ui/selectors/AuditorySelect2';
import { validateTemplate } from '~/shared/utils/validateTemplate';
import { defaultFormState } from '~/shared/const/templateDefaults';
import { useSnackbar } from 'notistack';
import { useEditTemplateCampaignMutation } from '~/features/templates/model/api/templates-api';
import { daysOfWeekEN } from '~/shared/const/app';
import { useCreateFileMutation } from '~/features/files/model/api/files-api';
import { buildTemplateData } from '~/shared/utils/buildTemplateData';
import { PlacementsGroup } from '../inputs/PlacementsGroup';
import { uploadImage, cropImageToAspect, getPreviewUrl } from '~/shared/utils/uploadImage';
import { LogoDropzone } from '~/shared/ui/inputs/LogoDropzone';
import { getFormattedList } from '~/shared/utils/googleConst';
import { buildFinalUrlSuffixPreview } from '~/shared/utils/buildFinalUrlSuffixPreview';
import {
    DEFAULT_CTA_LANGUAGE,
    findCallToActionKeyByText,
    normalizeCallToActionLanguage,
} from '~/shared/const/callToAction';
import { useGetCtaDictionaryOptionsQuery } from '~/features/dictionaries/model/api/cta-dictionary-api';
import { BudgetRangeSlider } from '~/shared/ui/inputs/BudgetRangeSlider';

const ImagePreview = ({ file, onDelete }: { file: File | Blob | string; onDelete?: () => void }) => {
    const [src, setSrc] = useState<string>('');

    useEffect(() => {
        if (typeof file === 'string') {
            setSrc(getPreviewUrl(file));
            return;
        }

        if (file instanceof Blob) {
            const url = URL.createObjectURL(file);
            setSrc(url);
            return () => URL.revokeObjectURL(url);
        }
    }, [file]);

    if (!src) return null;

    return (
        <Box sx={{ position: 'relative', width: 60, height: 60 }}>
            <Box
                component="img"
                src={src}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd' }}
            />
            {onDelete && (
                <IconButton
                    size="small"
                    onClick={onDelete}
                    sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                        width: 20,
                        height: 20,
                        '&:hover': { bgcolor: 'background.paper' }
                    }}
                >
                    <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
            )}
        </Box>
    );
};

export const EditTemplateModal = ({ editingTemplate, open, handleClose }: { editingTemplate: any, open: boolean; handleClose: () => void }) => {
    const [formState, setFormState] = useState<any>(defaultFormState);
    const [lang, setLang] = useState([]);
    const [schedule, setSchedule] = useState(editingTemplate?.schedule);
    const [uiState, setUiState] = useState({ visible: false, budgetError: false });
    const [logoUploading, setLogoUploading] = useState<{done: number; total: number} | null>(null);
    const [sourceImageUploading, setSourceImageUploading] = useState<{done: number; total: number} | null>(null);

    const [editTemplate, { isLoading }] = useEditTemplateCampaignMutation();

    const { enqueueSnackbar } = useSnackbar();
    const [uploadFile] = useCreateFileMutation();
    const { data: ctaDictionaryOptions } = useGetCtaDictionaryOptionsQuery();
    const ctaLanguages = useMemo(
        () => ctaDictionaryOptions?.data?.languages?.length
            ? ctaDictionaryOptions.data.languages
                .filter(item => item.is_active !== false)
                .map(item => ({ value: item.code, label: item.label }))
            : undefined,
        [ctaDictionaryOptions],
    );
    const ctaItems = useMemo(
        () => ctaDictionaryOptions?.data?.items?.length
            ? ctaDictionaryOptions.data.items.filter(item => item.is_active !== false)
            : undefined,
        [ctaDictionaryOptions],
    );

    const finalUrlSuffixPreview = useMemo(() => {
        return buildFinalUrlSuffixPreview({
            customerIdPlaceholder: '123-456-7890',
            appendTag: formState.append_tag,
            customUtm: formState.custom_utm,
            finalUrlSuffix: formState.final_url_suffix,
        });
    }, [formState.append_tag, formState.custom_utm, formState.final_url_suffix]);

    const isValid = useMemo(() => validateTemplate(formState, uiState.visible), [formState, uiState.visible]);

    useEffect(() => {
        if (editingTemplate) {
            const templateData = editingTemplate?.data && typeof editingTemplate.data === 'object'
                ? { ...editingTemplate.data, ...editingTemplate }
                : editingTemplate;
            const sanitizeMediaArray = (val: unknown) =>
                Array.isArray(val) ? val.filter(Boolean) : [];

            const dayToIndex = Object.entries(daysOfWeekEN).reduce((acc, [k, v]) => {
                acc[v] = +k;
                return acc;
            }, {} as Record<string, number>);

            const demography = templateData.demography || {};
            const genders = demography.genders || [];
            const age = demography.age || {};

            let geoValue = templateData.geo;
            if (typeof geoValue === 'string') {
                const found = getFormattedList.find((g: any) => g.name === geoValue);
                if (found) geoValue = found.id;
            }

            const legacyMarketing = sanitizeMediaArray(templateData.marketing_image);
            const squareMarketing = sanitizeMediaArray(templateData.marketing_image_square);
            const landscapeMarketing = sanitizeMediaArray(templateData.marketing_image_landscape);
            const portraitMarketing = sanitizeMediaArray(templateData.marketing_image_portrait);
            const sourceImages = sanitizeMediaArray(templateData.source_image);
            const legacySourceImages = Array.from(new Set([
                ...sourceImages,
                ...legacyMarketing,
                ...landscapeMarketing,
                ...squareMarketing,
                ...portraitMarketing,
            ]));
            const selectedFormats = Array.isArray(templateData.marketing_image_formats) && templateData.marketing_image_formats.length
                ? templateData.marketing_image_formats
                : [
                    ...(landscapeMarketing.length || legacyMarketing.length ? ['landscape'] : []),
                    ...(squareMarketing.length ? ['square'] : []),
                    ...(portraitMarketing.length ? ['portrait'] : []),
                ];
            const legacyAssetOptimization = typeof templateData.optimized_targeting === 'boolean'
                ? templateData.optimized_targeting
                : null;
            const legacyBudgetValues = Array.isArray(templateData.budget)
                ? templateData.budget.filter((value: unknown) => Number.isFinite(Number(value))).map((value: unknown) => Number(value))
                : [];
            const budgetUsdFrom = Number(templateData.budget_usd_from ?? (legacyBudgetValues.length ? Math.min(...legacyBudgetValues) : defaultFormState.budget_usd_from));
            const budgetUsdTo = Number(templateData.budget_usd_to ?? (legacyBudgetValues.length ? Math.max(...legacyBudgetValues) : defaultFormState.budget_usd_to));
            const callToActionLanguage = normalizeCallToActionLanguage(
                templateData.call_to_action_language ?? defaultFormState.call_to_action_language,
                ctaLanguages,
            );
            const callToActionKey = String(templateData.call_to_action_key || '').trim()
                || findCallToActionKeyByText(templateData.call_to_action, callToActionLanguage, ctaItems, ctaLanguages);

            setFormState((prev: any) => ({
                ...prev,
                ...defaultFormState,
                ...templateData,
                geo: geoValue ?? defaultFormState.geo,
                budget: Array.isArray(templateData.budget)
                    ? templateData.budget.join('\n')
                    : String(templateData.budget ?? ''),
                budget_usd_from: String(Number.isFinite(budgetUsdFrom) && budgetUsdFrom >= 1 ? budgetUsdFrom : defaultFormState.budget_usd_from),
                budget_usd_to: String(Number.isFinite(budgetUsdTo) && budgetUsdTo >= budgetUsdFrom ? budgetUsdTo : defaultFormState.budget_usd_to),
                budget_base_currency: 'USD',
                male: genders.includes('male') ? 'male' : '',
                female: genders.includes('female') ? 'female' : '',
                // поддерживаем legacy 'undefined' и новый 'unknown'
                unspecified_gender: (genders.includes('unknown') || genders.includes('undefined')) ? 'unknown' : '',
                ageFrom: age.from ?? null,
                ageTo: age.to ?? null,
                unspecified_age: age.undefined ?? false,
                optimized_targeting: legacyAssetOptimization,
                asset_automation_adaptive_layouts: typeof templateData.asset_automation_adaptive_layouts === 'boolean'
                    ? templateData.asset_automation_adaptive_layouts
                    : legacyAssetOptimization,
                asset_automation_generated_videos: typeof templateData.asset_automation_generated_videos === 'boolean'
                    ? templateData.asset_automation_generated_videos
                    : legacyAssetOptimization,
                append_tag: templateData.append_tag ?? defaultFormState.append_tag,
                custom_utm: templateData.append_tag ? (templateData.custom_utm ?? '') : '',
                final_url_suffix: templateData.final_url_suffix ?? '',
                call_to_action_key: callToActionKey,
                call_to_action_language: callToActionLanguage,
                logo: templateData.logo || [],
                source_image: legacySourceImages,
                marketing_image_formats: selectedFormats,
                placements: templateData.placements ?? prev.placements ?? {},
                headlines: Array.isArray(templateData.headlines)
                    ? templateData.headlines.join('\n')
                    : templateData.headlines ?? '',
                descriptions: Array.isArray(templateData.descriptions)
                    ? templateData.descriptions.join('\n')
                    : templateData.descriptions ?? '',
                marketing_link: Array.isArray(templateData.marketing_link)
                    ? templateData.marketing_link.join('\n')
                    : templateData.marketing_link ?? '',
            }));

            setUiState({
                visible: Array.isArray(templateData.marketing_link) && templateData.marketing_link.length > 0,
                budgetError: false,
            });

            setLang(templateData.languages ?? []);

            const transformedSchedule = (templateData.schedule || []).map((day: any) => ({
                day: dayToIndex[day.day], // ← возвращаем индекс дня
                from: {
                    hour: day.from?.hour ?? 0,
                    minute: day.from?.quarter != null ? (day.from.quarter / 15 + 2) : 2,
                },
                to: {
                    hour: day.to?.hour ?? 0,
                    minute: day.to?.quarter != null ? (day.to.quarter / 15 + 2) : 2,
                },
            }));

            setSchedule(transformedSchedule);
        }
    }, [open, editingTemplate, ctaItems, ctaLanguages]);

    const handleFormChange = useCallback((field: string, value: any) => {
        if (typeof value === 'function') {
            setFormState((prev: any) => ({ ...prev, [field]: value(prev[field]) }));
        } else {
            setFormState((prev: any) => ({ ...prev, [field]: value }));
        }
    }, []);

    const handleBudgetRangeChange = useCallback((value: [number, number]) => {
        const nextBudgetFrom = Math.max(1, Math.round(value[0]));
        const nextBudgetTo = Math.max(nextBudgetFrom, Math.round(value[1]));
        const hasError =
            !Number.isFinite(nextBudgetFrom) ||
            !Number.isFinite(nextBudgetTo) ||
            nextBudgetFrom < 1 ||
            nextBudgetTo < nextBudgetFrom;

        setUiState(prev => ({ ...prev, budgetError: hasError }));
        handleFormChange('budget_usd_from', String(nextBudgetFrom));
        handleFormChange('budget_usd_to', String(nextBudgetTo));
    }, [handleFormChange]);

    const syncLegacyAssetOptimization = useCallback((adaptiveLayouts: boolean | null, generatedVideos: boolean | null) => {
        return typeof adaptiveLayouts === 'boolean' &&
            typeof generatedVideos === 'boolean' &&
            adaptiveLayouts === generatedVideos
            ? adaptiveLayouts
            : null;
    }, []);

    const handleAssetAutomationChange = useCallback(
        (
            field: 'asset_automation_adaptive_layouts' | 'asset_automation_generated_videos',
            value: boolean | null,
        ) => {
            setFormState((prev: any) => {
                const next = { ...prev, [field]: value };
                next.optimized_targeting = syncLegacyAssetOptimization(
                    next.asset_automation_adaptive_layouts,
                    next.asset_automation_generated_videos,
                );
                return next;
            });
        },
        [syncLegacyAssetOptimization]
    );

    const handleUploadLogo = useCallback(
        async (file: File) => {
            await uploadImage({
                file,
                type: "logo",
                uploadFile,
                handleFormChange,
                formState,
                field: "logo",
                persistPath: true,
            });
        },
        [uploadFile, handleFormChange, formState],
    );

    const handleUploadLogos = useCallback(
        async (files: File[]) => {
            setLogoUploading({ done: 0, total: files.length });
            let done = 0;
            await files.reduce(async (chain, file) => {
                await chain;
                const cropped = await cropImageToAspect(file, 1).catch((error: any) => {
                    enqueueSnackbar(error?.message || `Не удалось обработать файл "${file.name}" перед загрузкой.`, {
                        variant: 'error',
                        autoHideDuration: 5000,
                        style: { maxWidth: '700px' },
                        anchorOrigin: { horizontal: 'right', vertical: 'top' },
                    });
                    return null;
                });
                if (cropped) {
                    await handleUploadLogo(cropped).catch(() => undefined);
                }
                done += 1;
                setLogoUploading({ done, total: files.length });
            }, Promise.resolve());
            setLogoUploading(null);
        },
        [handleUploadLogo],
    );

    const handleRemoveLogo = useCallback(
        (index: number) => {
            const arr = [...(formState.logo || [])];
            arr.splice(index, 1);
            handleFormChange('logo', arr);
        },
        [formState.logo, handleFormChange],
    );

    const handleUploadSourceImage = useCallback(
        async (file: File) => {
            try {
                setSourceImageUploading({ done: 0, total: 1 });
                await uploadImage({
                    file,
                    type: 'marketing_image',
                    uploadFile,
                    handleFormChange,
                    formState,
                    field: 'source_image',
                    persistPath: true,
                });
                setSourceImageUploading({ done: 1, total: 1 });
            } finally {
                // Даже если upload завершился ошибкой, убираем зависший индикатор.
                setTimeout(() => setSourceImageUploading(null), 300);
            }
        },
        [uploadFile, handleFormChange, formState],
    );

    const handleRemoveSourceImage = useCallback((index: number) => {
        const arr = [...(formState.source_image || [])];
        arr.splice(index, 1);
        handleFormChange('source_image', arr);
    }, [formState.source_image, handleFormChange]);

    const handleMarketingFormatToggle = useCallback((format: string) => {
        handleFormChange('marketing_image_formats', (prev: string[] = []) =>
            prev.includes(format)
                ? prev.filter(item => item !== format)
                : [...prev, format]
        );
    }, [handleFormChange]);

    const toggleModal = useCallback((key: string) => {
        setUiState((prev: any) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    // Отправка формы
    const submit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();

            const data = buildTemplateData(formState, uiState, {
                id: editingTemplate.id,
                schedule: schedule,
                daysOfWeekEN: daysOfWeekEN,
                lang: lang,
                callToActionItems: ctaItems,
                callToActionLanguages: ctaLanguages,
            });

            try {
                const payload = await editTemplate(data) as any;
                if (payload.error) {
                    enqueueSnackbar(payload.error.data.message, {
                        variant: 'error',
                        autoHideDuration: 3000,
                        style: { maxWidth: '600px' },
                        anchorOrigin: { horizontal: 'right', vertical: 'top' },
                    });
                    return;
                }

                // resetState();
                handleClose();
                enqueueSnackbar('Шаблон успешно изменен!', {
                    variant: 'success',
                    autoHideDuration: 3000,
                    style: { maxWidth: '600px' },
                    anchorOrigin: { horizontal: 'right', vertical: 'top' },
                });
            } catch (error: any) {
                enqueueSnackbar(error.data.message, {
                    variant: 'error',
                    autoHideDuration: 3000,
                    style: { maxWidth: '600px' },
                    anchorOrigin: { horizontal: 'right', vertical: 'top' },
                });
            }
        },
        [formState, uiState, schedule, lang, ctaItems, ctaLanguages, editingTemplate?.id, editTemplate, enqueueSnackbar, handleClose],
    );

    if (!editingTemplate) return null;

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 900,
                bgcolor: 'background.paper',
                borderRadius: '12px',
                boxShadow: 24,
                pt: 3,
                px: 3,
                pb: 3,
            }}>
                <form onSubmit={submit}>
                    <Stack flexDirection="row" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h5" component="h5">
                            Редактировать шаблон
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            * - Поля, обязательные для заполнения
                        </Typography>
                    </Stack>

                    <Box display="flex" gap={3} pb={0} sx={{ maxHeight: '70vh', overflowY: 'auto', px: 2 }}>
                        <Box flex={1} display="flex" flexDirection="column" gap={1.5}>
                            <Typography fontWeight={600} variant="subtitle2">Имя шаблона: *</Typography>
                            <TextField value={formState.name} onChange={e => handleFormChange('name', e.target.value)} />

                            <GeoSelect2 value={formState.geo} onChange={e => handleFormChange('geo', e.target.value)} />

                            <Box>
                                <Typography fontWeight={600} variant="subtitle2" mb={0.5}>
                                    Бюджет (USD): *
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Укажите диапазон в базовой валюте USD. Для каждого аккаунта итоговый бюджет будет выбран
                                    в этом диапазоне и затем автоматически конвертирован в валюту профиля.
                                </Typography>
                                <BudgetRangeSlider
                                    value={[
                                        Math.max(1, Number(formState.budget_usd_from) || 1),
                                        Math.max(1, Number(formState.budget_usd_to) || Number(formState.budget_usd_from) || 1),
                                    ]}
                                    onChange={handleBudgetRangeChange}
                                    error={uiState.budgetError}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                    Базовая валюта: USD
                                </Typography>
                            </Box>

                            <Box>
                                <Typography fontWeight={600} variant="subtitle2" mb={0.5}>
                                    Asset optimization
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Настройки повторяют блок Google Ads: image и video automation
                                    управляются отдельно, а при сбросе используется поведение Google по умолчанию.
                                </Typography>
                                <Stack spacing={1}>
                                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                            <Box>
                                                <Typography fontWeight={600}>Image</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Adaptive layouts
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={0.5}>
                                                <Button
                                                    size="small"
                                                    variant={formState.asset_automation_adaptive_layouts === true ? 'contained' : 'outlined'}
                                                    onClick={() => handleAssetAutomationChange('asset_automation_adaptive_layouts', true)}
                                                >
                                                    On
                                                </Button>
                                                <Button
                                                    size="small"
                                                    color={formState.asset_automation_adaptive_layouts === false ? 'inherit' : 'primary'}
                                                    variant={formState.asset_automation_adaptive_layouts === false ? 'contained' : 'outlined'}
                                                    onClick={() => handleAssetAutomationChange('asset_automation_adaptive_layouts', false)}
                                                >
                                                    Off
                                                </Button>
                                            </Stack>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
                                            <Typography variant="caption" color="text.secondary">
                                                Сейчас: {formState.asset_automation_adaptive_layouts === null
                                                    ? 'По умолчанию Google'
                                                    : formState.asset_automation_adaptive_layouts ? 'On' : 'Off'}
                                            </Typography>
                                            <Button
                                                size="small"
                                                sx={{ px: 0, textTransform: 'none' }}
                                                onClick={() => handleAssetAutomationChange('asset_automation_adaptive_layouts', null)}
                                            >
                                                По умолчанию
                                            </Button>
                                        </Stack>
                                    </Box>

                                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                            <Box>
                                                <Typography fontWeight={600}>Video</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Generated videos
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={0.5}>
                                                <Button
                                                    size="small"
                                                    variant={formState.asset_automation_generated_videos === true ? 'contained' : 'outlined'}
                                                    onClick={() => handleAssetAutomationChange('asset_automation_generated_videos', true)}
                                                >
                                                    On
                                                </Button>
                                                <Button
                                                    size="small"
                                                    color={formState.asset_automation_generated_videos === false ? 'inherit' : 'primary'}
                                                    variant={formState.asset_automation_generated_videos === false ? 'contained' : 'outlined'}
                                                    onClick={() => handleAssetAutomationChange('asset_automation_generated_videos', false)}
                                                >
                                                    Off
                                                </Button>
                                            </Stack>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
                                            <Typography variant="caption" color="text.secondary">
                                                Сейчас: {formState.asset_automation_generated_videos === null
                                                    ? 'По умолчанию Google'
                                                    : formState.asset_automation_generated_videos ? 'On' : 'Off'}
                                            </Typography>
                                            <Button
                                                size="small"
                                                sx={{ px: 0, textTransform: 'none' }}
                                                onClick={() => handleAssetAutomationChange('asset_automation_generated_videos', null)}
                                            >
                                                По умолчанию
                                            </Button>
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Box>

                            <MultipleTextInput
                                title="Заголовки: *"
                                maxRows={50}
                                value={formState.headlines}
                                isError={false}
                                onChange={e => handleFormChange('headlines', e.target.value)}
                            />

                            <MultipleTextInput
                                title="Описания: *"
                                maxRows={50}
                                value={formState.descriptions}
                                isError={false}
                                onChange={e => handleFormChange('descriptions', e.target.value)}
                            />

                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6" component="h6" sx={{ fontSize: '14px' }}>
                                    Логотипы (1:1): *
                                </Typography>
                                <Typography variant="body2">{(formState.logo || []).length} файлов</Typography>
                            </Stack>
                            <LogoDropzone
                                value={formState.logo || []}
                                onFiles={handleUploadLogos}
                                onRemove={handleRemoveLogo}
                                uploading={logoUploading}
                                maxFilesPerBatch={30}
                            />
                            
                            <Box display="flex" alignItems="center">
                                <Typography>Marketing изображение</Typography>
                                <Switch checked={uiState.visible} onChange={() => toggleModal('visible')} sx={{ mx: 1 }} />
                                <Typography>Ссылка на YouTube</Typography>
                            </Box>

                            {!uiState.visible ? (
                                <>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                        Загрузите исходное изображение и выберите форматы, которые нужно автоматически подготовить.
                                        Для запуска Demand Gen нужен минимум один из форматов: 1:1 или 1.91:1.
                                    </Typography>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="h6" component="h6" sx={{ fontSize: '14px' }}>
                                            Marketing image:
                                        </Typography>
                                        <Typography variant="body2">{(formState.source_image || []).length} файлов</Typography>
                                    </Stack>
                                    <Button component="label" variant="outlined">
                                        + Добавить исходное изображение
                                        <input
                                            hidden
                                            type="file"
                                            accept="image/png, image/jpeg"
                                            onChange={async e => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    await handleUploadSourceImage(file);
                                                }
                                                e.currentTarget.value = '';
                                            }}
                                        />
                                    </Button>
                                    {sourceImageUploading && (
                                        <Box mb={1}>
                                            <Typography variant="caption">Загрузка: {sourceImageUploading.done}/{sourceImageUploading.total}</Typography>
                                            <LinearProgress variant="determinate" value={Math.min(100, Math.round((sourceImageUploading.done / sourceImageUploading.total) * 100))} sx={{ mt: 0.5 }} />
                                        </Box>
                                    )}
                                    <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                                        {(formState.source_image || []).map((item: File | string, index: number) => (
                                            <ImagePreview
                                                key={index}
                                                file={item}
                                                onDelete={() => handleRemoveSourceImage(index)}
                                            />
                                        ))}
                                    </Box>

                                    <Box p={1.5} border="1px solid" borderColor="divider" borderRadius={2}>
                                        <Typography fontWeight={600} variant="subtitle2" mb={0.5}>
                                            Форматы адаптации
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                            Система автоматически подготовит версии под отмеченные aspect ratio из исходного изображения.
                                        </Typography>
                                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                            <CheckboxWithLabel
                                                value={(formState.marketing_image_formats || []).includes('square')}
                                                title="1:1"
                                                onChange={() => handleMarketingFormatToggle('square')}
                                            />
                                            <CheckboxWithLabel
                                                value={(formState.marketing_image_formats || []).includes('landscape')}
                                                title="1.91:1"
                                                onChange={() => handleMarketingFormatToggle('landscape')}
                                            />
                                            <CheckboxWithLabel
                                                value={(formState.marketing_image_formats || []).includes('portrait')}
                                                title="4:5"
                                                onChange={() => handleMarketingFormatToggle('portrait')}
                                            />
                                            <CheckboxWithLabel
                                                value={(formState.marketing_image_formats || []).includes('story')}
                                                title="9:16"
                                                onChange={() => handleMarketingFormatToggle('story')}
                                            />
                                        </Stack>
                                    </Box>
                                </>
                            ) : (
                                <MultipleTextInput
                                    title="Ссылка на YouTube:"
                                    maxRows={50}
                                    value={formState.marketing_link}
                                    isError={false}
                                    onChange={e => handleFormChange('marketing_link', e.target.value)}
                                />
                            )}

                            {/* <Box display="flex" flexDirection="column" gap={1}>
                                <Box display="flex" alignItems="center" gap={0}>
                                    <Checkbox
                                        checked={formState.append_tag}
                                        onChange={e => handleFormChange('append_tag', e.target.checked)}
                                    />
                                    <Typography fontWeight={600} variant="subtitle2">Добавить tag конверсии</Typography>
                                </Box>
                                {formState.append_tag && (
                                    <Box p={1.5} bgcolor="action.hover" borderRadius={1}>
                                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                            {`{lpurl}?tag=${import.meta.env.VITE_CONVERSION_TAG || 'YOUR_TAG'}`}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            
                            <Accordion disableGutters elevation={0} sx={{ border: '1px solid #ddd', borderRadius: 1, '&:before': { display: 'none' } }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography fontWeight={600} variant="subtitle2">Campaign URL options</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box display="flex" flexDirection="column" gap={2}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Tracking Template</Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="https://www.trackingtemplate.foo/?url={lpurl}&id=5"
                                                value={formState.tracking_template || ''}
                                                onChange={e => handleFormChange('tracking_template', e.target.value)}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Final URL suffix</Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="param1=value1&param2=value2"
                                                value={formState.final_url_suffix || ''}
                                                onChange={e => handleFormChange('final_url_suffix', e.target.value)}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Custom Parameters</Typography>
                                            {(formState.custom_parameters || []).map((param: any, index: number) => (
                                                <Box key={index} display="flex" gap={1} mb={1} alignItems="center">
                                                    <TextField
                                                        size="small"
                                                        placeholder="{_Name}"
                                                        value={param.key}
                                                        onChange={e => {
                                                            const newParams = [...(formState.custom_parameters || [])];
                                                            newParams[index].key = e.target.value;
                                                            handleFormChange('custom_parameters', newParams);
                                                        }}
                                                    />
                                                    <Typography>=</Typography>
                                                    <TextField
                                                        size="small"
                                                        placeholder="Value"
                                                        value={param.value}
                                                        onChange={e => {
                                                            const newParams = [...(formState.custom_parameters || [])];
                                                            newParams[index].value = e.target.value;
                                                            handleFormChange('custom_parameters', newParams);
                                                        }}
                                                    />
                                                    <IconButton size="small" color="error" onClick={() => {
                                                        const newParams = [...(formState.custom_parameters || [])];
                                                        newParams.splice(index, 1);
                                                        handleFormChange('custom_parameters', newParams);
                                                    }}>
                                                        <RemoveCircleIcon />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                            <Button
                                                startIcon={<AddCircleIcon />}
                                                size="small"
                                                onClick={() => {
                                                    handleFormChange('custom_parameters', [...(formState.custom_parameters || []), { key: '', value: '' }]);
                                                }}
                                            >
                                                Add parameter
                                            </Button>
                                        </Box>
                                    </Box>
                                </AccordionDetails>
                            </Accordion> */}
                        </Box>

                        <Box flex={1} display="flex" flexDirection="column" gap={1.5}>

                            <PlacementsGroup
                                formState={formState}
                                setFormState={setFormState}
                            />

                            <CallToAction
                                value={formState.call_to_action_key}
                                language={formState.call_to_action_language || DEFAULT_CTA_LANGUAGE}
                                onChange={e => handleFormChange('call_to_action_key', e.target.value)}
                                onLanguageChange={e => handleFormChange('call_to_action_language', e.target.value)}
                            />

                            <AuditorySelect2 handleFormChange={handleFormChange} formState={formState} />

                            <ScheduleModal schedule={schedule} setSchedule={setSchedule} />

                            <DevicesSelect
                                friendly
                                devices={formState.devices}
                                handleFormChange={handleFormChange}
                            />

                            <LanguageSelector2 lang={lang} setLang={setLang} />

                            <AgeInput
                                ageFrom={formState.ageFrom ?? ''}
                                onChangeFrom={e => handleFormChange('ageFrom', Number(e.target.value) || null)}
                                ageTo={formState.ageTo ?? ''}
                                onChangeTo={e => handleFormChange('ageTo', Number(e.target.value) || null)}
                                unspecified_age={formState.unspecified_age}
                                onChangeUnspecifiedAge={e => handleFormChange('unspecified_age', e.target.checked)}
                            />
                            <Box mt={1} p={1.5} bgcolor="action.hover" borderRadius={1}>
                                <CheckboxWithLabel
                                    value={formState.append_tag}
                                    title="Автоматически вставлять тег конверсии"
                                    onChange={e => handleFormChange('append_tag', e.target.checked)}
                                />
                                <Typography fontWeight={600} variant="subtitle2" mb={0.5}>Тег конверсии</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="AW-123456789/AbCdEfGhIjK"
                                    value={formState.custom_utm || ''}
                                    onChange={e => handleFormChange('custom_utm', e.target.value)}
                                    helperText={
                                        formState.append_tag
                                            ? 'Если поле пустое, тег будет подставлен автоматически. Если заполнено, будет использовано значение из поля.'
                                            : 'Автоподстановка выключена. Если поле заполнено, будет использовано значение из поля; если пустое, тег не добавится.'
                                    }
                                />
                            </Box>
                            <Box>
                                <Typography fontWeight={600} variant="subtitle2" mb={0.5}>Final URL suffix</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="utm_source=google&utm_medium=cpc"
                                    value={formState.final_url_suffix || ''}
                                    onChange={e => handleFormChange('final_url_suffix', e.target.value)}
                                    helperText="Пользовательский suffix будет добавлен после системных параметров a и tag."
                                />
                                <Typography fontWeight={600} variant="subtitle2" mb={0.5} mt={1}>Предпросмотр итогового suffix</Typography>
                                <Box
                                    sx={{
                                        px: 1.5,
                                        py: 1.25,
                                        borderRadius: 1,
                                        bgcolor: 'action.hover',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.95rem',
                                            wordBreak: 'break-all',
                                        }}
                                    >
                                        {finalUrlSuffixPreview}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    В шаблоне показан пример отображения с маской.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box display="flex" gap={1.5} pt={1.5}>
                        <Button fullWidth variant="outlined" color="secondary" onClick={handleClose}>
                            Отмена
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            type="submit"
                            disabled={!isValid || isLoading}
                        >
                            {isLoading && <CircularProgress size={24} sx={{ mr: 1 }} />}
                            Сохранить
                        </Button>
                    </Box>
                </form>
            </Box>
        </Modal>
    );
};
